import { useEffect, useMemo, useRef, useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import Text from '@/shared-components/text/components/Text';

import './styles/RatingBarDisplayer.css';

function toSafePercentage(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return 0;
  return Math.min(100, Math.max(0, Math.floor(parsedValue)));
}

function getIndicatorTextAlignmentStyle(percentage) {
  if (percentage >= 92) {
    return {
      left: 'auto',
      right: 0,
      transform: 'none',
      textAlign: 'right',
    };
  }

  if (percentage <= 8) {
    return {
      left: 0,
      right: 'auto',
      transform: 'none',
      textAlign: 'left',
    };
  }

  return {
    left: '50%',
    right: 'auto',
    transform: 'translateX(-50%)',
    textAlign: 'center',
  };
}

export default function RatingBarDisplayer({ username, userRating, myRating, ignoreRightOverflow=false, includeGuide=false }) {
  const { state } = useStateHandler();
  const [textStyle, setTextStyle] = useState(null);
  const userRatingPercentage = useMemo(() => toSafePercentage(userRating), [userRating]);
  const myRatingPercentage = useMemo(() => toSafePercentage(myRating), [myRating]);
  const artworkRatingDisplayerContainer = useRef();
  const myIndicatorTextStyle = useMemo(
    () => getIndicatorTextAlignmentStyle(myRatingPercentage),
    [myRatingPercentage],
  );
  const userIndicatorAlignmentStyle = useMemo(
    () => getIndicatorTextAlignmentStyle(userRatingPercentage),
    [userRatingPercentage],
  );

  useEffect(() => {
    if(ignoreRightOverflow) return;

    const timeoutId = setTimeout(() => {
      const container = artworkRatingDisplayerContainer.current;
      if (!container) return;

      const leftWidth = container.clientWidth * (1-(userRatingPercentage/100)) * 2;
      setTextStyle({ maxWidth: `${leftWidth + 40}px` });
    }, 50);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [ignoreRightOverflow, userRatingPercentage]);

  return (
    <>
      <div className="artwork-rating-displayer__range-container" style={{  height: ` 5px ` }} ref={artworkRatingDisplayerContainer}>
        <div style={{ width: `${myRatingPercentage}%` , height: ` 5px ` }} className="artwork-rating-displayer__bar my-rating"/>
        <div style={{ width: `${userRatingPercentage}%` }} className="artwork-rating-displayer__bar user-rating"/>

        <div style={{ left: `${myRatingPercentage}%` }}  className="artwork-rating-displayer__indicator my-rating">
          <div className="artwork-rating-displayer__indicator-bar my-rating"/>

          <Text
            className="artwork-rating-displayer__indicator-text my-rating"
            style={myIndicatorTextStyle}
            paragraph
            extraSmallPlus
          >
                        you
          </Text>
        </div>
            
        {state.user_session?.user_username !== username && (
          <div className="artwork-rating-displayer__indicator" style={{ left: `${userRatingPercentage}%` }}>
            <Text
              className="artwork-rating-displayer__indicator-text dots-on-overflow"
              style={{ ...(textStyle || {}), ...userIndicatorAlignmentStyle }}
              paragraph
              extraSmallPlus
            >
              {username}
            </Text>
                        
            <div className="artwork-rating-displayer__indicator-bar"/>
          </div>
        )}

        {includeGuide && <div className="artwork-rating-displayer__guide-container">
          <div style={{ left: "12.5%" }} className="artwork-rating-displayer__guide-indicator"/>
          <div style={{ left: "25%" }} className="artwork-rating-displayer__guide-indicator"/>
          <div style={{ left: "37.5%" }} className="artwork-rating-displayer__guide-indicator"/>
          <div style={{ left: "50%" }} className="artwork-rating-displayer__guide-indicator main"/>
          <div style={{ left: "62.5%" }} className="artwork-rating-displayer__guide-indicator"/>
          <div style={{ left: "75%" }} className="artwork-rating-displayer__guide-indicator"/>
          <div style={{ left: "87.5%" }} className="artwork-rating-displayer__guide-indicator"/>
        </div>}
      </div>
    </>
  );
}
